/**
 * Shared state for the onboarding wizard (Phase 1).
 *
 * Each wizard screen reads + mutates a slice of this state, then
 * advances. On the final screen (Keyholder soft-ask), we POST the
 * collected WizardAnswersDto to /api/onboarding/answers and store the
 * response (priority task keys + declined IDs + LegalZoom CTAs +
 * successor seed) so the Priorities Reveal screen can render it.
 *
 * Storing state in context (rather than navigation params) keeps
 * screens decoupled — each screen updates its own slice without
 * having to thread the entire payload forward.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';

export type HouseholdAnswers = {
  hasSpouse: boolean;
  hasChildren: boolean;
  hasPets: boolean;
};

export type ProfessionalsAnswers = {
  hasPhysician: boolean;
  hasAttorney: boolean;
  hasInvestmentAdvisor: boolean;
  hasAccountant: boolean;
};

export type EstateDocAnswer = {
  hasDocument: boolean;
  wantsToCreate?: boolean;
};

export type DocumentsAnswers = {
  will: EstateDocAnswer;
  trust: EstateDocAnswer;
  advanceDirective: EstateDocAnswer;
  powerOfAttorney: EstateDocAnswer;
};

export type SuccessorAnswer = {
  firstName?: string;
  email?: string;
};

export type WizardAnswers = {
  household: HouseholdAnswers;
  professionals: ProfessionalsAnswers;
  documents: DocumentsAnswers;
  successor?: SuccessorAnswer;
};

export type WizardSubmissionResult = {
  priorityTaskKeys: string[];
  declinedTaskIds: number[];
  legalZoomDocTypes: string[];
  successorSeed: SuccessorAnswer | null;
  /** True when wizard submission auto-started the user's 14-day trial. */
  trialJustStarted: boolean;
};

const defaultAnswers: WizardAnswers = {
  household: { hasSpouse: false, hasChildren: false, hasPets: false },
  professionals: {
    hasPhysician: false,
    hasAttorney: false,
    hasInvestmentAdvisor: false,
    hasAccountant: false,
  },
  documents: {
    will:              { hasDocument: false, wantsToCreate: false },
    trust:             { hasDocument: false, wantsToCreate: false },
    advanceDirective:  { hasDocument: false, wantsToCreate: false },
    powerOfAttorney:   { hasDocument: false, wantsToCreate: false },
  },
};

type WizardContextValue = {
  answers: WizardAnswers;
  setHousehold: (h: HouseholdAnswers) => void;
  setProfessionals: (p: ProfessionalsAnswers) => void;
  setDocuments: (d: DocumentsAnswers) => void;
  setSuccessor: (k?: SuccessorAnswer) => void;
  submission: WizardSubmissionResult | null;
  setSubmission: (s: WizardSubmissionResult) => void;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [answers, setAnswers] = useState<WizardAnswers>(defaultAnswers);
  const [submission, setSubmission] = useState<WizardSubmissionResult | null>(null);

  const value = useMemo<WizardContextValue>(
    () => ({
      answers,
      setHousehold:     household =>      setAnswers(a => ({ ...a, household })),
      setProfessionals: professionals =>  setAnswers(a => ({ ...a, professionals })),
      setDocuments:     documents =>      setAnswers(a => ({ ...a, documents })),
      setSuccessor:     successor =>      setAnswers(a => ({ ...a, successor })),
      submission,
      setSubmission,
    }),
    [answers, submission],
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizard = (): WizardContextValue => {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside <WizardProvider>');
  return ctx;
};
